import cx from "classnames";
import React from "react";

import GridItemFooter, {
    GridItemFooterPlaceholder,
} from "@/mk2/apps/bazaar/components/GridItemFooter";
import GridItemHeader, {
    GridItemHeaderPlaceholder,
} from "@/mk2/apps/bazaar/components/GridItemHeader";
import GridItemInfo, { GridItemInfoPlaceholder } from "@/mk2/apps/bazaar/components/GridItemInfo";
import GridItemPhoto, {
    GridItemPhotoPlaceholder,
} from "@/mk2/apps/bazaar/components/GridItemPhoto";
import { slugToParams } from "@/mk2/apps/bazaar/helpers";
import { ProductEntity } from "@/mk2/apps/bazaar/schemas";
import { bazaarProductDetailUrl } from "@/mk2/apps/bazaar/urls";
import { Link } from "@/mk2/components/Link";
import { url } from "@/mk2/helpers/urls";
import useEventCallback from "@/mk2/hooks/useEventCallback";

import styles from "./GridItem.mscss";

interface OwnProps {
    className?: string;
    style?: React.CSSProperties;
    product: ProductEntity;
    showCreatedTime?: boolean;
    showStarredTime?: boolean;
    isModerator?: boolean;
    isSeller?: boolean;
    checked?: boolean;
    complaintsCount?: number;
    showStateBadges?: boolean;
    onClick?(product: ProductEntity);
}

type Props = OwnProps;

const GridItem: React.FunctionComponent<Props> = ({
    className,
    style,
    product,
    showCreatedTime = false,
    showStarredTime = false,
    isModerator = false,
    isSeller = false,
    complaintsCount,
    checked,
    showStateBadges = false,
    onClick,
}) => {
    const detailUrl = url(bazaarProductDetailUrl, slugToParams(product.slug));

    const onClickCallback = useEventCallback(() => {
        if (onClick) {
            onClick(product);
        }
    }, [onClick, product]);

    const stateClassNames = {};
    if (showStateBadges) {
        Object.keys(product.state).forEach((state) => {
            stateClassNames[styles[`GridItem--${state}`]] = product.state[state];
        });
    }

    return (
        <div
            className={cx(styles.GridItem, className, stateClassNames)}
            style={style}
            data-cy="bazaar-grid-item"
        >
            <Link to={detailUrl} className={styles.GridItem__link} onClick={onClickCallback}>
                <GridItemHeader
                    product={product}
                    isModerator={isModerator}
                    checked={checked}
                    complaintsCount={complaintsCount}
                    showCreatedTime={showCreatedTime}
                    showStarredTime={showStarredTime}
                />
                <GridItemInfo product={product} showStateBadges={showStateBadges} />
                <GridItemPhoto product={product} />
            </Link>
            <GridItemFooter seller={product.seller} isSeller={isSeller} />
        </div>
    );
};

GridItem.displayName = "GridItem";

export default React.memo<Props>(GridItem);

interface PlaceholderOwnProps {
    className?: string;
    style?: React.CSSProperties;
}

let GridItemPlaceholder: React.FunctionComponent<PlaceholderOwnProps> = ({ className, style }) => (
    <div className={cx(styles.GridItem, className)} style={style}>
        <span>
            <GridItemHeaderPlaceholder />
            <GridItemInfoPlaceholder />
            <GridItemPhotoPlaceholder />
        </span>
        <GridItemFooterPlaceholder />
    </div>
);

GridItemPlaceholder.displayName = "GridItemPlaceholder";

GridItemPlaceholder = React.memo<PlaceholderOwnProps>(GridItemPlaceholder);

export { GridItemPlaceholder };
